import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  Goal,
  KeyResult,
  WeeklyCheckin,
  CreateGoalRequest,
  UpdateGoalRequest,
  CreateKeyResultRequest,
  UpdateKeyResultRequest,
  CreateCheckinRequest,
  UpdateCheckinRequest,
  GoalStatus,
} from "@/models/okr";
import {
  getGoals,
  getGoalsByOwner,
  getGoalsByStatus,
  getGoalById,
  createGoal,
  updateGoal,
  deleteGoal,
  getKeyResults,
  getKeyResultById,
  createKeyResult,
  updateKeyResult,
  deleteKeyResult,
  getCheckins,
  getCheckinsByUser,
  getCheckinsByWeek,
  getCheckinById,
  getUserCheckinForWeek,
  createCheckin,
  updateCheckin,
  deleteCheckin,
  getGoalsSummary,
  getRecentCheckins,
  getActiveGoals,
} from "@/helpers/server-actions/okr-collection";

export const okrApi = createApi({
  reducerPath: "okrApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Goal", "KeyResult", "Checkin"],
  endpoints: (build) => ({
    // ========================================================================
    // Goal Endpoints
    // ========================================================================

    getGoals: build.query<Goal[], string>({
      async queryFn(workspaceId) {
        try {
          const goals = await getGoals(workspaceId);
          return { data: goals };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, workspaceId) => [
        { type: "Goal", id: workspaceId },
      ],
    }),

    getGoalsByOwner: build.query<Goal[], { workspaceId: string; ownerId: string }>({
      async queryFn({ workspaceId, ownerId }) {
        try {
          const goals = await getGoalsByOwner(workspaceId, ownerId);
          return { data: goals };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { workspaceId }) => [
        { type: "Goal", id: workspaceId },
      ],
    }),

    getGoalsByStatus: build.query<Goal[], { workspaceId: string; status: GoalStatus }>({
      async queryFn({ workspaceId, status }) {
        try {
          const goals = await getGoalsByStatus(workspaceId, status);
          return { data: goals };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { workspaceId }) => [
        { type: "Goal", id: workspaceId },
      ],
    }),

    getGoalById: build.query<Goal | null, { workspaceId: string; goalId: string }>({
      async queryFn({ workspaceId, goalId }) {
        try {
          const goal = await getGoalById(workspaceId, goalId);
          return { data: goal };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { goalId }) => [{ type: "Goal", id: goalId }],
    }),

    getActiveGoals: build.query<Goal[], string>({
      async queryFn(workspaceId) {
        try {
          const goals = await getActiveGoals(workspaceId);
          return { data: goals };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, workspaceId) => [
        { type: "Goal", id: workspaceId },
      ],
    }),

    createGoal: build.mutation<Goal, { request: CreateGoalRequest; creatorId: string }>({
      async queryFn({ request, creatorId }) {
        try {
          const goal = await createGoal(request, creatorId);
          return { data: goal };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { request }) => [
        { type: "Goal", id: request.workspaceId },
      ],
    }),

    updateGoal: build.mutation<Goal | null, UpdateGoalRequest>({
      async queryFn(request) {
        try {
          const goal = await updateGoal(request);
          return { data: goal };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId, goalId }) => [
        { type: "Goal", id: workspaceId },
        { type: "Goal", id: goalId },
      ],
    }),

    deleteGoal: build.mutation<boolean, { workspaceId: string; goalId: string }>({
      async queryFn({ workspaceId, goalId }) {
        try {
          const result = await deleteGoal(workspaceId, goalId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Goal", id: workspaceId },
        "KeyResult",
      ],
    }),

    // ========================================================================
    // Key Result Endpoints
    // ========================================================================

    getKeyResults: build.query<KeyResult[], { workspaceId: string; goalId: string }>({
      async queryFn({ workspaceId, goalId }) {
        try {
          const keyResults = await getKeyResults(workspaceId, goalId);
          return { data: keyResults };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { goalId }) => [{ type: "KeyResult", id: goalId }],
    }),

    getKeyResultById: build.query<
      KeyResult | null,
      { workspaceId: string; goalId: string; keyResultId: string }
    >({
      async queryFn({ workspaceId, goalId, keyResultId }) {
        try {
          const keyResult = await getKeyResultById(workspaceId, goalId, keyResultId);
          return { data: keyResult };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { keyResultId }) => [
        { type: "KeyResult", id: keyResultId },
      ],
    }),

    createKeyResult: build.mutation<
      KeyResult,
      { request: CreateKeyResultRequest; creatorId: string }
    >({
      async queryFn({ request, creatorId }) {
        try {
          const keyResult = await createKeyResult(request, creatorId);
          return { data: keyResult };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { request }) => [
        { type: "KeyResult", id: request.goalId },
        { type: "Goal", id: request.goalId },
        { type: "Goal", id: request.workspaceId },
      ],
    }),

    updateKeyResult: build.mutation<KeyResult | null, UpdateKeyResultRequest>({
      async queryFn(request) {
        try {
          const keyResult = await updateKeyResult(request);
          return { data: keyResult };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { goalId, keyResultId, workspaceId }) => [
        { type: "KeyResult", id: goalId },
        { type: "KeyResult", id: keyResultId },
        { type: "Goal", id: goalId },
        { type: "Goal", id: workspaceId },
      ],
    }),

    deleteKeyResult: build.mutation<
      boolean,
      { workspaceId: string; goalId: string; keyResultId: string }
    >({
      async queryFn({ workspaceId, goalId, keyResultId }) {
        try {
          const result = await deleteKeyResult(workspaceId, goalId, keyResultId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { goalId, workspaceId }) => [
        { type: "KeyResult", id: goalId },
        { type: "Goal", id: goalId },
        { type: "Goal", id: workspaceId },
      ],
    }),

    // ========================================================================
    // Check-in Endpoints
    // ========================================================================

    getCheckins: build.query<WeeklyCheckin[], string>({
      async queryFn(workspaceId) {
        try {
          const checkins = await getCheckins(workspaceId);
          return { data: checkins };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, workspaceId) => [
        { type: "Checkin", id: workspaceId },
      ],
    }),

    getCheckinsByUser: build.query<
      WeeklyCheckin[],
      { workspaceId: string; userId: string }
    >({
      async queryFn({ workspaceId, userId }) {
        try {
          const checkins = await getCheckinsByUser(workspaceId, userId);
          return { data: checkins };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { workspaceId }) => [
        { type: "Checkin", id: workspaceId },
      ],
    }),

    getCheckinsByWeek: build.query<
      WeeklyCheckin[],
      { workspaceId: string; weekStartDate: string }
    >({
      async queryFn({ workspaceId, weekStartDate }) {
        try {
          const checkins = await getCheckinsByWeek(
            workspaceId,
            new Date(weekStartDate)
          );
          return { data: checkins };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { workspaceId }) => [
        { type: "Checkin", id: workspaceId },
      ],
    }),

    getCheckinById: build.query<
      WeeklyCheckin | null,
      { workspaceId: string; checkinId: string }
    >({
      async queryFn({ workspaceId, checkinId }) {
        try {
          const checkin = await getCheckinById(workspaceId, checkinId);
          return { data: checkin };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { checkinId }) => [
        { type: "Checkin", id: checkinId },
      ],
    }),

    getUserCheckinForWeek: build.query<
      WeeklyCheckin | null,
      { workspaceId: string; userId: string; weekStartDate: string }
    >({
      async queryFn({ workspaceId, userId, weekStartDate }) {
        try {
          const checkin = await getUserCheckinForWeek(
            workspaceId,
            userId,
            new Date(weekStartDate)
          );
          return { data: checkin };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { workspaceId }) => [
        { type: "Checkin", id: workspaceId },
      ],
    }),

    getRecentCheckins: build.query<
      WeeklyCheckin[],
      { workspaceId: string; limit?: number }
    >({
      async queryFn({ workspaceId, limit = 10 }) {
        try {
          const checkins = await getRecentCheckins(workspaceId, limit);
          return { data: checkins };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, { workspaceId }) => [
        { type: "Checkin", id: workspaceId },
      ],
    }),

    createCheckin: build.mutation<WeeklyCheckin, CreateCheckinRequest>({
      async queryFn(request) {
        try {
          const checkin = await createCheckin(request);
          return { data: checkin };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Checkin", id: workspaceId },
      ],
    }),

    updateCheckin: build.mutation<WeeklyCheckin | null, UpdateCheckinRequest>({
      async queryFn(request) {
        try {
          const checkin = await updateCheckin(request);
          return { data: checkin };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId, checkinId }) => [
        { type: "Checkin", id: workspaceId },
        { type: "Checkin", id: checkinId },
      ],
    }),

    deleteCheckin: build.mutation<boolean, { workspaceId: string; checkinId: string }>({
      async queryFn({ workspaceId, checkinId }) {
        try {
          const result = await deleteCheckin(workspaceId, checkinId);
          return { data: result };
        } catch (e) {
          return { error: e };
        }
      },
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: "Checkin", id: workspaceId },
      ],
    }),

    // ========================================================================
    // Summary / Dashboard Endpoints
    // ========================================================================

    getGoalsSummary: build.query<
      {
        total: number;
        byStatus: Record<GoalStatus, number>;
        averageProgress: number;
      },
      string
    >({
      async queryFn(workspaceId) {
        try {
          const summary = await getGoalsSummary(workspaceId);
          return { data: summary };
        } catch (e) {
          return { error: e };
        }
      },
      providesTags: (_result, _error, workspaceId) => [
        { type: "Goal", id: workspaceId },
      ],
    }),
  }),
});

export const {
  // Goals
  useGetGoalsQuery,
  useLazyGetGoalsQuery,
  useGetGoalsByOwnerQuery,
  useGetGoalsByStatusQuery,
  useGetGoalByIdQuery,
  useLazyGetGoalByIdQuery,
  useGetActiveGoalsQuery,
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useDeleteGoalMutation,
  // Key Results
  useGetKeyResultsQuery,
  useLazyGetKeyResultsQuery,
  useGetKeyResultByIdQuery,
  useCreateKeyResultMutation,
  useUpdateKeyResultMutation,
  useDeleteKeyResultMutation,
  // Checkins
  useGetCheckinsQuery,
  useLazyGetCheckinsQuery,
  useGetCheckinsByUserQuery,
  useGetCheckinsByWeekQuery,
  useGetCheckinByIdQuery,
  useGetUserCheckinForWeekQuery,
  useLazyGetUserCheckinForWeekQuery,
  useGetRecentCheckinsQuery,
  useCreateCheckinMutation,
  useUpdateCheckinMutation,
  useDeleteCheckinMutation,
  // Summary
  useGetGoalsSummaryQuery,
} = okrApi;

export default okrApi;
